import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setUserInfo,
  setCompanyInfo,
} from "../../../../store/registrationSlice";
import EditBrandModal from "../../../../components/modals/EditBrandModal";
import EditCompanyModal from "../../../../components/modals/EditCompanyModal";
import EditUserModal from "../../../../components/modals/EditUserModal";
import ImageCropModal from "../../../../components/modals/ImageCropModal";
import LocationModal from "../../../../components/modals/LocationModal";
import DeleteModal from "../../../../components/modals/DeleteModal";
import Location from "../../../../api/Location";
import ReactImageUploading from "react-images-uploading";
import Brand from "../../../../api/Brand";
import User from "../../../../api/User";
import { toast } from "react-hot-toast";

const PreviewRegistration = ({ onClose, registrationResponse }) => {
  const dispatch = useDispatch();
  const [isEditBrandModalOpen, setIsEditBrandModalOpen] = useState(false);
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isAddLocationModalOpen, setIsAddLocationModalOpen] = useState(false);
  const [updatedUserData, setUpdatedUserData] = useState(null);
  const [images, setImages] = useState([]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState("");
  const [crop, setCrop] = useState({
    unit: "px",
    width: 100,
    height: 100,
    x: 10,
    y: 10,
    aspect: 1,
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  const { userInfo, companyInfo, locations } = useSelector((state) => ({
    userInfo: state.registration.userInfo,
    companyInfo: state.registration.companyInfo,
    locations: state.registration.locations,
  }));

  const handleEditBrandClick = () => {
    setIsEditBrandModalOpen(true);
  };

  const handleEditUserClick = () => {
    setIsEditUserModalOpen(true);
  };

  const handleUserUpdate = async (userData) => {
    try {
      const updateData = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone,
      };
      const response = await User.update(
        updateData,
        registrationResponse.user.id
      );
      const updatedData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phone,
      };
      setUpdatedUserData(updatedData);
      dispatch(setUserInfo(updatedData));
      toast.success("User details updated successfully");
      setIsEditUserModalOpen(false);
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(
        error.response?.data?.message || "Error updating user details"
      );
      throw error; // Re-throw to handle in the modal
    }
  };

  const uploadBrandPhoto = async (brandId, imageFile) => {
    try {
      await Brand.uploadImage(brandId, imageFile);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error uploading logo");
    }
  };

  const handleCompanyUpdate = (companyData) => {
    // Update the Redux store with the new company data
    dispatch(
      setCompanyInfo({
        ...companyInfo,
        companyName: companyData.companyName,
        isEnterprise: companyData.isEnterprise,
      })
    );
    setIsEditCompanyModalOpen(false);
  };

  const openCropModal = (imageDataURL, selectedFile) => {
    setImageToCrop(imageDataURL);
    setSelectedFileData(selectedFile);
    setCrop({
      unit: "px",
      width: 100,
      height: 100,
      x: 10,
      y: 10,
      aspect: 1,
    });
    setShowCropModal(true);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleCropSave = async (croppedResult) => {
    if (croppedResult) {
      const croppedFile = new File(
        [croppedResult.file],
        selectedFileData?.name || "brand-logo.jpg",
        { type: croppedResult.file.type }
      );
      try {
        const response = await Brand.uploadImage(
          registrationResponse.brand.id,
          croppedFile
        );
        toast.success("Logo uploaded successfully");
        dispatch(
          setCompanyInfo({
            ...companyInfo,
            images: [
              { dataURL: response.data?.data?.thumb_url || response.data.data },
            ],
          })
        );
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Error uploading logo");
      }
      setShowCropModal(false);
    }
  };

  const handleBrandUpdate = (brandData) => {
    dispatch(
      setCompanyInfo({
        ...companyInfo,
        companyName: brandData.name,
      })
    );
    setIsEditBrandModalOpen(false);
  };

  return (
    <div className="bg-white p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Registration Preview
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* User Information */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            User Information
          </h3>
          <button
            onClick={handleEditUserClick}
            className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md"
          >
            Edit User
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">First Name</p>
            <p className="mt-1">
              {updatedUserData?.firstName || userInfo.firstName}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Last Name</p>
            <p className="mt-1">
              {updatedUserData?.lastName || userInfo.lastName}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Email</p>
            <p className="mt-1">{updatedUserData?.email || userInfo.email}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Phone</p>
            <p className="mt-1">{updatedUserData?.phone || userInfo.phone}</p>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Company Information
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditCompanyModalOpen(true)}
              className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md"
            >
              Edit Company
            </button>
            <button
              onClick={() => setIsEditBrandModalOpen(true)}
              className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md"
            >
              Edit Brand
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Company Name</p>
            <p className="mt-1">{companyInfo.companyName}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Account Type</p>
            <p className="mt-1">
              {companyInfo.isEnterprise ? "Enterprise" : "Standard"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">
              How did you hear about us?
            </p>
            <p className="mt-1">
              {companyInfo.hearAbout === "Others"
                ? companyInfo.otherSource
                : companyInfo.hearAbout}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Company Logo</p>
            <div className="mt-1 flex items-center gap-4">
              <div className="h-16 w-16 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                {companyInfo.images?.[0]?.dataURL ? (
                  <img
                    src={companyInfo.images[0].dataURL}
                    alt="Company Logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <i className="text-gray-400 text-xl fa fa-image" />
                )}
              </div>
              <ReactImageUploading
                value={images}
                onChange={(imageList) => {
                  const selectedFile = imageList[0]?.file;
                  if (selectedFile && selectedFile.type === "image/svg+xml") {
                    toast.error("SVG files are not allowed");
                  } else {
                    if (imageList[0]) {
                      openCropModal(imageList[0].dataURL, selectedFile);
                    } else {
                      setImages([]);
                    }
                  }
                }}
                maxNumber={1}
              >
                {({ onImageUpload }) => (
                  <button
                    type="button"
                    onClick={onImageUpload}
                    className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md"
                  >
                    Upload Logo
                  </button>
                )}
              </ReactImageUploading>
            </div>
          </div>
        </div>
      </div>

      {/* Locations */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Locations</h3>
          <button
            onClick={() => setIsAddLocationModalOpen(true)}
            className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md flex items-center gap-2"
          >
            <i className="far fa-plus" />
            Add Location
          </button>
        </div>
        <div className="space-y-6">
          {locations.map((location, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <h4 className="text-lg font-medium text-gray-900">
                  {location.businessName}
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Address</p>
                  <p className="mt-1">{location.businessAddress}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Categories
                  </p>
                  <p className="mt-1">{location.categories.join(", ")}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Contact Number
                  </p>
                  <p className="mt-1">{location.contactNumber}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500">
                    Description
                  </p>
                  <p className="mt-1">{location.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditCompanyModal
        isOpen={isEditCompanyModalOpen}
        onClose={() => setIsEditCompanyModalOpen(false)}
        initialCompanyData={{
          id: registrationResponse?.brand?.company_id,
          companyName: companyInfo.companyName,
          isEnterprise: companyInfo.isEnterprise,
        }}
        onSave={handleCompanyUpdate}
      />

      <EditBrandModal
        isOpen={isEditBrandModalOpen}
        onClose={() => setIsEditBrandModalOpen(false)}
        initialBrandData={{
          brand_id: registrationResponse?.brand?.id,
          name: registrationResponse?.brand?.name || companyInfo.companyName,
        }}
        onSave={handleBrandUpdate}
      />

      <ImageCropModal
        showCropModal={showCropModal}
        handleCropSave={handleCropSave}
        handleCropCancel={handleCropCancel}
        imageToCrop={imageToCrop}
        crop={crop}
        setCrop={setCrop}
        completedCrop={completedCrop}
        setCompletedCrop={setCompletedCrop}
      />

      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        initialUserData={registrationResponse?.user || userInfo}
        onSave={handleUserUpdate}
      />

      <LocationModal
        isOpen={isAddLocationModalOpen}
        onClose={() => setIsAddLocationModalOpen(false)}
        onSave={(locationData) => {
          // Add new location
          dispatch({
            type: "registration/addLocation",
            payload: {
              businessName: locationData.name,
              businessAddress: locationData.address,
              contactNumber: locationData.contact_number,
              categories: locationData.categories,
              description: locationData.description,
            },
          });
        }}
      />
    </div>
  );
};

export default PreviewRegistration;
